import { Box, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { formatCurrency } from '@/utils/helper';

/** A donation backs a single option, so every option shares one native radio group. */
const RADIO_GROUP_NAME = 'bidwarChoice';

const visuallyHidden = {
  position: 'absolute',
  overflow: 'hidden',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  border: 0,
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
};

/** @param {import("prop-types").InferProps<typeof BidwarOptionCardPropTypes>} props */
export default function BidwarOptionCard({
  bidwarId,
  option,
  isSelected,
  isOther = false,
  isLeading = false,
  currentPercent,
  projectedPercent,
  currentChoice = null,
  onSelect,
  onCustomChange,
}) {
  const { t, i18n } = useTranslation();

  const getOptionLabel = () => {
    if (isOther) {
      return t('donation.customChoice');
    }

    return i18n.language === 'fr' ? option.labelFr : option.labelEn;
  };

  return (
    <Box>
      <Box
        component="label"
        sx={[
          {
            display: 'block',
            width: '100%',
            padding: 1,
            cursor: 'pointer',
            borderRadius: '2px',
            borderLeft: '2px solid transparent',
            transition: 'background-color 0.2s ease, border-color 0.2s ease',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
            '&:has(input:focus-visible)': {
              outline: '2px solid',
              outlineColor: 'warning.main',
              outlineOffset: '2px',
            },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          },
          isSelected && {
            borderLeftColor: 'success.main',
            backgroundColor: 'rgba(255,255,255,0.05)',
          },
        ]}
      >
        <Box
          component="input"
          type="radio"
          name={RADIO_GROUP_NAME}
          checked={isSelected}
          sx={visuallyHidden}
          onChange={() => onSelect(bidwarId, option.id, isOther)}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 1,
            marginBottom: 0.75,
          }}
        >
          <Typography
            className="font-oswald"
            sx={[
              {
                fontSize: '0.95rem',
                fontWeight: 500,
                lineHeight: 1.2,
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.75)',
              },
              isLeading && { color: 'warning.main' },
              isSelected && { color: 'common.white' },
            ]}
          >
            {getOptionLabel()}
          </Typography>
          {!isOther && (
            <Typography
              className="font-bebas"
              sx={{
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                color: 'rgba(255,255,255,0.6)',
                whiteSpace: 'nowrap',
              }}
            >
              {formatCurrency(option.total, i18n.language)}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            overflow: 'hidden',
            width: '100%',
            height: 6,
            borderRadius: '1px',
            backgroundColor: 'rgba(255,255,255,0.08)',
          }}
        >
          {/* What this option has raised so far */}
          <Box
            sx={[
              {
                width: `${currentPercent}%`,
                height: '100%',
                backgroundColor: 'rgba(255,255,255,0.22)',
                transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              },
              isLeading && {
                backgroundColor: 'warning.main',
                boxShadow: theme => `0 0 8px ${theme.palette.warning.main}60`,
              },
            ]}
          />
          {/* What this donation would add to it */}
          {projectedPercent > 0 && (
            <Box
              sx={{
                width: `${projectedPercent}%`,
                height: '100%',
                backgroundColor: 'success.main',
                boxShadow: theme => `0 0 10px ${theme.palette.success.main}50`,
                opacity: 0.7,
                transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            />
          )}
        </Box>
      </Box>

      {isOther && currentChoice?.isOther && (
        <TextField
          fullWidth
          size="small"
          sx={{ marginTop: 1 }}
          placeholder={t('donation.customChoiceInput')}
          value={currentChoice?.customValue || ''}
          onChange={event => onCustomChange(bidwarId, event.target.value)}
        />
      )}
    </Box>
  );
}

const BidwarOptionCardPropTypes = {
  bidwarId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  currentChoice: PropTypes.shape({
    isOther: PropTypes.bool,
    customValue: PropTypes.string,
  }),
  currentPercent: PropTypes.number.isRequired,
  isLeading: PropTypes.bool,
  isOther: PropTypes.bool,
  isSelected: PropTypes.bool.isRequired,
  onCustomChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  option: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    labelEn: PropTypes.string,
    labelFr: PropTypes.string,
    total: PropTypes.number,
  }).isRequired,
  projectedPercent: PropTypes.number.isRequired,
};
BidwarOptionCard.propTypes = BidwarOptionCardPropTypes;
